import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { Button } from '@/design-system/button';
import { cn } from '@/design-system/lib/utils';

import { fmtQty } from '../format';
import type { ClientCatalog, SkuOption } from '../hooks/useCatalog';
import type { WarehouseSku } from '../types';
import { TextInput } from './form';
import { SkuSelect } from './pickers';

/**
 * SKU × qty line rows shared by the outbound request and PO forms. Each row's
 * SKU is a server-searched picker (no whole-catalog load); SKUs already used
 * on another line are left out of its options. Rows are memoised: typing a
 * qty re-renders that row only.
 */

export interface SkuLineDraft {
  key: number;
  sku: string;
  qty: string;
}

let lineSeq = 0;
/** A fresh line with a unique React key. */
export const newSkuLine = (sku = ''): SkuLineDraft => ({
  key: ++lineSeq,
  sku,
  qty: '',
});

/** Lines the user touched (SKU or qty set). */
export const filledSkuLines = <L extends SkuLineDraft>(lines: L[]): L[] =>
  lines.filter((l) => l.sku || l.qty.trim());

/** "12,5" / "12.5" → 12.5; blank or junk → null. */
export const parseQty = (s: string): number | null => {
  const t = s.trim().replace(',', '.');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

type LinePatch = Partial<Omit<SkuLineDraft, 'key'>>;

type LineCatalog = Pick<
  ClientCatalog,
  | 'clientID'
  | 'firstPage'
  | 'skuBy'
  | 'loading'
  | 'error'
  | 'availableFor'
  | 'remember'
>;

interface RowProps {
  line: SkuLineDraft;
  index: number;
  catalog: LineCatalog;
  used: ReadonlySet<string>;
  extraOptions: SkuOption[];
  uom: string | undefined;
  /** Allocatable qty in the hub; undefined = column hidden, null = unknown. */
  available: number | null | undefined;
  error: string | null;
  canRemove: boolean;
  selectDisabled: boolean;
  selectPlaceholder: string;
  qtyMin: number;
  qtyStep?: string;
  qtyPlaceholder: string;
  removeSize: 'icon-xs' | 'icon-sm';
  grid: string;
  /** Moves focus to this row's qty each time it changes to a new non-zero value. */
  focusQtyToken: number;
  onChange: (key: number, patch: LinePatch) => void;
  onRemove: (key: number) => void;
  onExtraOption?: (value: string, lineKey: number, query: string) => void;
}

const SkuLineRow = React.memo(function SkuLineRow({
  line,
  index,
  catalog,
  used,
  extraOptions,
  uom,
  available,
  error,
  canRemove,
  selectDisabled,
  selectPlaceholder,
  qtyMin,
  qtyStep,
  qtyPlaceholder,
  removeSize,
  grid,
  focusQtyToken,
  onChange,
  onRemove,
  onExtraOption,
}: RowProps) {
  const qtyRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (focusQtyToken) qtyRef.current?.focus();
  }, [focusQtyToken]);
  const exclude = useMemo(() => {
    if (!line.sku || !used.has(line.sku)) return used;
    const others = new Set(used);
    others.delete(line.sku);
    return others;
  }, [used, line.sku]);
  const { remember } = catalog;
  const onPick = useCallback(
    (s: WarehouseSku) => {
      remember(s);
      onChange(line.key, { sku: s.sku });
    },
    [remember, onChange, line.key]
  );
  const onExtra = useCallback(
    (v: string, query: string) => onExtraOption?.(v, line.key, query),
    [onExtraOption, line.key]
  );
  const qty = parseQty(line.qty);
  const short =
    available !== null &&
    available !== undefined &&
    qty !== null &&
    qty > available;
  const n = index + 1;

  return (
    <div className="flex flex-col gap-1">
      <div className={grid}>
        <SkuSelect
          aria-label={`SKU baris ${n}`}
          data-testid={`sku-line-${n}`}
          catalog={catalog}
          value={line.sku}
          onPick={onPick}
          exclude={exclude}
          extraOptions={extraOptions}
          onExtraOption={onExtra}
          placeholder={selectPlaceholder}
          disabled={selectDisabled}
          invalid={!!error}
        />
        <TextInput
          ref={qtyRef}
          aria-label={`Qty baris ${n}`}
          type="number"
          size="md"
          min={qtyMin}
          step={qtyStep}
          value={line.qty}
          onChange={(v) => onChange(line.key, { qty: v })}
          placeholder={qtyPlaceholder}
          suffix={uom}
          invalid={!!error}
        />
        {available !== undefined && (
          <span
            className={cn(
              'text-[12px] tabular-nums',
              short
                ? 'font-medium text-(--state-warning-dark)'
                : 'text-text-sub-600'
            )}
          >
            {available === null ? (
              '—'
            ) : (
              <>
                {short && <i className="ri-alert-line mr-1" aria-hidden />}
                tersedia {fmtQty(available)}
              </>
            )}
          </span>
        )}
        {canRemove ? (
          <Button
            tone="neutral"
            style="ghost"
            size={removeSize}
            aria-label={`Hapus baris ${n}`}
            title="Hapus baris"
            onClick={() => onRemove(line.key)}
          >
            <i className="ri-delete-bin-line text-[14px]" />
          </Button>
        ) : (
          <span aria-hidden />
        )}
      </div>
      {error && (
        <p className="text-[11px] text-(--state-error-base)" role="alert">
          Baris {n}: {error}
        </p>
      )}
    </div>
  );
});

interface SkuLinesEditorProps<L extends SkuLineDraft> {
  lines: L[];
  setLines: Dispatch<SetStateAction<L[]>>;
  catalog: LineCatalog;
  /** "Stok di hub" column + column headers (outbound request). */
  showAvailability?: boolean;
  selectDisabled?: boolean;
  selectPlaceholder: string;
  /** Options after the catalog (PO: "+ SKU baru…"). Pass a stable array. */
  extraOptions?: SkuOption[];
  /**
   * Picked instead of setting the SKU when an extra option is chosen; `query`
   * is what the user had typed in that line's search.
   */
  onExtraOption?: (value: string, lineKey: number, query: string) => void;
  /**
   * Focus the qty of line `key` — each new `seq` focuses again (e.g. after a
   * SKU was created for that line).
   */
  focusQty?: { key: number; seq: number } | null;
  /** Inline error of a line row (by index), e.g. from a server 422. */
  lineError?: (index: number) => string | null;
  qtyMin?: number;
  qtyStep?: string;
  qtyPlaceholder?: string;
  removeSize?: 'icon-xs' | 'icon-sm';
}

const NO_EXTRA: SkuOption[] = [];

export function SkuLinesEditor<L extends SkuLineDraft>({
  lines,
  setLines,
  catalog,
  showAvailability = false,
  selectDisabled = false,
  selectPlaceholder,
  extraOptions = NO_EXTRA,
  onExtraOption,
  focusQty = null,
  lineError,
  qtyMin = 0,
  qtyStep,
  qtyPlaceholder = '0',
  removeSize = 'icon-sm',
}: SkuLinesEditorProps<L>) {
  // Identity changes only when the set of picked SKUs does — not on qty keys.
  const usedKey = lines
    .map((l) => l.sku)
    .filter(Boolean)
    .sort()
    .join('\n');
  const used = useMemo(
    () => new Set(usedKey ? usedKey.split('\n') : []),
    [usedKey]
  );
  const onChange = useCallback(
    (key: number, patch: LinePatch) =>
      setLines((prev) =>
        prev.map((l) => (l.key === key ? { ...l, ...patch } : l))
      ),
    [setLines]
  );
  const onRemove = useCallback(
    (key: number) => setLines((prev) => prev.filter((l) => l.key !== key)),
    [setLines]
  );

  const grid = showAvailability
    ? 'grid grid-cols-[minmax(0,1fr)_110px_150px_32px] items-center gap-2'
    : 'grid grid-cols-[minmax(0,1fr)_110px_32px] items-center gap-2';

  return (
    <>
      {showAvailability && (
        <div className="grid grid-cols-[minmax(0,1fr)_110px_150px_32px] gap-2 px-0.5 text-[11px] font-medium text-text-soft-400">
          <span>SKU</span>
          <span>Qty</span>
          <span>Stok di hub</span>
          <span />
        </div>
      )}
      {lines.map((l, i) => (
        <SkuLineRow
          key={l.key}
          line={l}
          index={i}
          catalog={catalog}
          used={used}
          extraOptions={extraOptions}
          uom={l.sku ? catalog.skuBy.get(l.sku)?.uomName : undefined}
          available={
            showAvailability
              ? l.sku
                ? catalog.availableFor(l.sku)
                : null
              : undefined
          }
          error={lineError?.(i) ?? null}
          canRemove={lines.length > 1}
          selectDisabled={selectDisabled}
          selectPlaceholder={selectPlaceholder}
          qtyMin={qtyMin}
          qtyStep={qtyStep}
          qtyPlaceholder={qtyPlaceholder}
          removeSize={removeSize}
          grid={grid}
          focusQtyToken={focusQty?.key === l.key ? focusQty.seq : 0}
          onChange={onChange}
          onRemove={onRemove}
          onExtraOption={onExtraOption}
        />
      ))}
    </>
  );
}
